import React, { useState } from "react";

const partners = [
  {
    id: "sparkedhost",
    name: "SparkedHost",
    url: "https://sparked.host/afterpartylabs",
    banner: "/images/sparkedhost-afterparty25.webp",
    code: "AFTERPARTY25",
    offer: "25% off the first month.",
    description:
      "SparkedHost provides hosting solutions for Discord bots, game servers, VPS infrastructure, and other applications.",
  },
];

function PartnerCard({ partner }) {
  const [feedback, setFeedback] = useState("");
  const [copying, setCopying] = useState(false);

  async function copyCode() {
    setCopying(true);
    setFeedback("");
    try {
      await navigator.clipboard.writeText(partner.code);
      setFeedback(`Copied ${partner.code} to clipboard.`);
    } catch {
      setFeedback(`Unable to copy automatically. Please select and copy ${partner.code} manually.`);
    } finally {
      setCopying(false);
    }
  }

  return (
    <article aria-labelledby={`${partner.id}-name`} className="overflow-hidden rounded-2xl border border-apb-border-soft bg-apb-bg-card shadow-apb-glass">
      <img
        src={partner.banner}
        alt={`${partner.name} hosting: use ${partner.code} for ${partner.offer}`}
        width="1200"
        height="300"
        loading="lazy"
        decoding="async"
        className="block h-auto w-full"
      />
      <div className="p-6 sm:p-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 max-w-xl">
            <h3 id={`${partner.id}-name`} className="text-2xl font-bold text-white">{partner.name}</h3>
            <p className="mt-3 text-base leading-relaxed text-apb-text-secondary">{partner.description}</p>
          </div>
          <a href={partner.url} target="_blank" rel="noopener noreferrer sponsored" className="apb-btn inline-flex min-h-[44px] shrink-0 items-center justify-center px-5 py-3 text-sm font-semibold focus-ring-neo">
            Explore {partner.name}<span className="sr-only"> (opens in a new tab)</span>
          </a>
        </div>
        <div className="mt-6 rounded-xl border border-apb-border-soft bg-black/20 p-4 sm:p-5">
          <p className="font-semibold text-apb-accent-cyan">{partner.offer}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="text-sm text-apb-text-secondary">Community code</span>
            <code className="select-all rounded-lg border border-apb-accent-cyan/30 bg-apb-bg-surface px-3 py-2 text-base font-bold tracking-wide text-white">{partner.code}</code>
            <button type="button" onClick={copyCode} disabled={copying} aria-label={`Copy discount code ${partner.code}`} className="min-h-[44px] rounded-lg border border-apb-border-soft px-4 py-2 text-sm font-semibold text-white transition-colors hover:border-apb-accent-cyan hover:bg-white/10 focus-ring-neo disabled:opacity-60">
              {copying ? "Copying…" : "Copy code"}
            </button>
          </div>
          <p role="status" aria-live="polite" aria-atomic="true" className="mt-2 min-h-[1.5rem] text-sm leading-relaxed text-apb-text-secondary">{feedback}</p>
        </div>
        <p className="mt-5 text-sm leading-relaxed text-apb-text-secondary">
          Afterparty Bot Labs may earn a commission from qualifying purchases made through our partner link, at no additional cost to you.
        </p>
      </div>
    </article>
  );
}

export default function HostingPartners() {
  return (
    <section aria-labelledby="hosting-partners-heading" className="mx-auto mb-16 max-w-6xl px-6">
      <h2 id="hosting-partners-heading" className="text-center text-3xl font-black tracking-tight text-white apb-glow-text">Official Hosting Partners</h2>
      <p className="mt-2 text-center text-apb-text-secondary">Supporting our Discord development community.</p>
      <div className="mt-8 grid gap-8">
        {partners.map((partner) => <PartnerCard key={partner.id} partner={partner} />)}
      </div>
    </section>
  );
}
